import { inject } from 'aurelia';
import { IRouter } from '@aurelia/router';
import { I18N } from '@aurelia/i18n';
import { EventApi } from '../../apis/event/event-api';
import { EventEntity } from '../../apis/event/event-entity';
import { GroupEntity } from '../../apis/group/group-entity';
import { UserContext } from '../../contexts/user-context';
import { AssociationApi, MyRegistration, MyRegistrationTeam } from '../../apis/association/association-api';
import { PresentApi } from '../../apis/present/present-api';

interface RegistrationTeamRow {
    suffix: string;
    isDuplicate?: boolean;
}

@inject(IRouter, EventApi, PresentApi, UserContext, AssociationApi, I18N)
export class RegistrationPage {
    public eventCode: string = '';
    public event: EventEntity | null = null;
    public groups: GroupEntity[] = [];
    public selectedGroupId: string | null = null;
    public teamRows: RegistrationTeamRow[] = [];
    public associationName: string = '';
    public associationConfirmed: boolean = false;
    public isLoading: boolean = false;
    public isBusy: boolean = false;
    public isRegistering: boolean = false;
    public error: boolean = false;
    public validationMessage: string | null = null;
    public successMessage: string | null = null;

    public myRegistrations: MyRegistration[] = [];

    constructor(
        private readonly _router: IRouter,
        private readonly _eventApi: EventApi,
        private readonly _presentApi: PresentApi,
        private readonly _userContext: UserContext,
        private readonly _associationApi: AssociationApi,
        private readonly _i18n: I18N,
    ) {}

    public loading(params: Record<string, string>): void {
        this.eventCode = (params?.code ?? '').toUpperCase();
        this.successMessage = null;
        this.validationMessage = null;
    }

    public async attached(): Promise<void> {
        if (!this.eventCode) {
            this.error = true;
            return;
        }

        this.isLoading = true;
        try {
            const presentation = await this._presentApi.getByCode(this.eventCode);
            this.event = presentation.event;
            this.groups = presentation.groups;
            if (this._userContext.isLoggedIn) {
                await this._loadMyRegistrations();
            }
            this.error = false;
        } catch {
            this.error = true;
        } finally {
            this.isLoading = false;
        }
    }

    private async _loadMyRegistrations(): Promise<void> {
        const registrations = await this._associationApi.getMyRegistrations(this.eventCode);
        this.myRegistrations = registrations.map(r => ({
            ...r,
            newMemberEmail: '',
            newMemberRole: 'Contact',
            teams: r.teams.map(t => ({ ...t, isDuplicate: false })),
        }));
        this.myRegistrations.forEach(registration => this.updateExistingTeamDuplicateFlags(registration));
    }

    public get registrationOpen(): boolean {
        if (!this.event) {
            return false;
        }

        if (!this.event.registrationEnabled) {
            return false;
        }

        if (!this.event.registrationExpirationDate) {
            return true;
        }

        const expiry = new Date(this.event.registrationExpirationDate.toISOString());
        return expiry.getTime() > Date.now();
    }

    public get registrationExpired(): boolean {
        return !!this.event?.registrationEnabled && !!this.event.registrationExpirationDate && !this.registrationOpen;
    }

    public get availableGroupsForNewRegistration(): GroupEntity[] {
        const registeredGroupIds = new Set(this.myRegistrations.map(r => r.groupId));
        return this.groups.filter(g => !registeredGroupIds.has(g.id));
    }

    public get directLinkUrl(): string {
        return `${window.location.origin}/${this.eventCode}`;
    }

    public get directLinkDisplay(): string {
        return `${window.location.host}/${this.eventCode}`;
    }

    public get locationNotSpecifiedText(): string {
        return this._i18n.tr('ui:registration_location_not_specified');
    }

    public get noDescriptionText(): string {
        return this._i18n.tr('ui:registration_no_description');
    }

    public get registrationGroupFallbackText(): string {
        return this._i18n.tr('ui:registration_group_fallback');
    }

    public get associationFallbackText(): string {
        return this._i18n.tr('ui:association');
    }

    public roleLabel(role: string): string {
        return this._i18n.tr(role === 'Manager' ? 'ui:role_manager' : 'ui:role_contact');
    }

    public isManager(registration: MyRegistration): boolean {
        return registration.myRole === 'Manager';
    }

    public canEditRegistration(registration: MyRegistration): boolean {
        return this.registrationOpen && this.isManager(registration);
    }

    public groupName(groupId: string): string {
        return this.groups.find(g => g.id === groupId)?.name ?? this.registrationGroupFallbackText;
    }

    public selectGroup(groupId: string): void {
        this.selectedGroupId = this.selectedGroupId === groupId ? null : groupId;
        this.teamRows = [];
        this.associationName = '';
        this.associationConfirmed = false;
    }

    public get canConfirmAssociation(): boolean {
        return !!this.associationName?.trim();
    }

    // Auto-adds a single empty-suffix team so the flow matches edit-teams: name first, then teams.
    public confirmAssociation(): void {
        if (!this.canConfirmAssociation) { return; }
        this.associationConfirmed = true;
        this.teamRows = [{ suffix: '' }];
        this.updateTeamRowDuplicateFlags();
    }

    public addTeamRow(): void {
        if (!this.selectedGroupId) {
            return;
        }

        this.teamRows.push({ suffix: '' });
        this.updateTeamRowDuplicateFlags();
    }

    public removeTeamRow(index: number): void {
        this.teamRows.splice(index, 1);
        this.updateTeamRowDuplicateFlags();
    }

    // Recomputes each row's isDuplicate flag; called on every mutation/edit since template interpolations
    // don't reactively observe cross-row method calls (only plain property reads are reliably reactive).
    public updateTeamRowDuplicateFlags(): void {
        const counts = new Map<string, number>();
        this.teamRows.forEach(r => {
            const key = r.suffix ?? '';
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        this.teamRows.forEach(r => {
            r.isDuplicate = (counts.get(r.suffix ?? '') ?? 0) > 1;
        });
    }

    public updateExistingTeamDuplicateFlags(registration: MyRegistration): void {
        const counts = new Map<string, number>();
        registration.teams.forEach(team => {
            const key = team.suffix ?? '';
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        registration.teams.forEach(team => {
            team.isDuplicate = (counts.get(team.suffix ?? '') ?? 0) > 1;
        });
        registration.hasDuplicateTeamSuffixes = registration.teams.some(team => team.isDuplicate);
    }

    public get hasDuplicateTeamSuffixes(): boolean {
        return this.teamRows.some(r => r.isDuplicate);
    }

    public loginToRegister(): void {
        void this._router.load(`login?returnUrl=${encodeURIComponent(`registration/${this.eventCode}`)}`);
    }

    public async saveRegistration(): Promise<void> {
        if (!this.event || !this.selectedGroupId || !this.associationName || this.teamRows.length === 0) {
            this.validationMessage = this._i18n.tr('ui:registration_validation_message');
            return;
        }

        const payload = {
            eventId: this.event.id,
            eventCode: this.event.code,
            groupId: this.selectedGroupId,
            associationName: this.associationName,
            coaches: [],
            teams: this.teamRows.map(row => ({ suffix: row.suffix })),
            savedAt: new Date().toISOString(),
        };

        this.isRegistering = true;
        this.validationMessage = null;
        try {
            const result = await this._eventApi.submitRegistration(this.event.code, payload);
            if (result.statusCode >= 200 && result.statusCode < 300) {
                this.successMessage = this._i18n.tr('ui:registration_save_success');
                this.selectedGroupId = null;
                this.teamRows = [];
                this.associationName = '';
                this.associationConfirmed = false;
                await this._loadMyRegistrations();
            } else {
                this.validationMessage = this._i18n.tr('ui:registration_save_failed');
            }
        } catch (error: any) {
            this.validationMessage = error?.content?.message ?? this._i18n.tr('ui:registration_save_failed');
        } finally {
            this.isRegistering = false;
        }
    }

    public async saveAssociationName(registration: MyRegistration): Promise<void> {
        if (!this.canEditRegistration(registration)) {
            return;
        }

        this.isBusy = true;
        try {
            await this._associationApi.rename(registration.associationId, registration.associationName);
        } finally {
            this.isBusy = false;
        }
    }

    public async addExistingTeamRow(registration: MyRegistration): Promise<void> {
        this.isBusy = true;
        try {
            const team = await this._associationApi.addTeam(registration.associationId, '');
            registration.teams.push({ ...team, isDuplicate: false });
            this.updateExistingTeamDuplicateFlags(registration);
        } finally {
            this.isBusy = false;
        }
    }

    public async renameExistingTeam(registration: MyRegistration, team: MyRegistrationTeam): Promise<void> {
        this.isBusy = true;
        try {
            await this._associationApi.renameTeam(registration.associationId, team.id, team.suffix);
        } finally {
            this.isBusy = false;
        }
    }

    public async removeExistingTeam(registration: MyRegistration, teamId: string): Promise<void> {
        await this._associationApi.removeTeam(registration.associationId, teamId);
        registration.teams = registration.teams.filter(t => t.id !== teamId);
        this.updateExistingTeamDuplicateFlags(registration);
    }

    public async inviteExistingMember(registration: MyRegistration): Promise<void> {
        if (!registration.newMemberEmail) {
            return;
        }

        this.isBusy = true;
        try {
            const member = await this._associationApi.inviteMember(registration.associationId, registration.newMemberEmail, registration.newMemberRole ?? 'Contact');
            registration.members.push(member);
            registration.newMemberEmail = '';
        } finally {
            this.isBusy = false;
        }
    }

    public async removeExistingMember(registration: MyRegistration, memberId: string): Promise<void> {
        await this._associationApi.removeMember(registration.associationId, memberId);
        registration.members = registration.members.filter(m => m.id !== memberId);
    }

    public async acceptMyInvite(registration: MyRegistration): Promise<void> {
        await this._associationApi.acceptInvite(registration.associationId);
        registration.isAccepted = true;
        const selfMember = registration.members.find(m => m.isSelf);
        if (selfMember) { selfMember.isAccepted = true; }
    }

    public async leaveRegistration(registration: MyRegistration): Promise<void> {
        this.isBusy = true;
        try {
            await this._associationApi.leave(registration.associationId);
            this.myRegistrations = this.myRegistrations.filter(r => r.associationId !== registration.associationId);
        } catch (error: any) {
            if (error?.content?.message === 'cannot_remove_last_manager') {
                window.alert(this._i18n.tr('ui:registration_cannot_leave_last_manager'));
            }
        } finally {
            this.isBusy = false;
        }
    }

    public async revokeRegistration(registration: MyRegistration): Promise<void> {
        const confirmation = this._i18n.tr('ui:registration_revoke_confirmation');
        if (!window.confirm(confirmation)) { return; }

        this.isBusy = true;
        try {
            await this._associationApi.revoke(registration.associationId);
            this.myRegistrations = this.myRegistrations.filter(r => r.associationId !== registration.associationId);
        } finally {
            this.isBusy = false;
        }
    }

    public back(): void {
        void this._router.load('present');
    }
}
